import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { withTheme } from 'styled-components';
import { useThrottle } from '@react-hook/throttle';
import { getColumnsWidthForBreakpoint, getGridColumn } from '../helpers/grid';
import { getValueByScreenSize, Breakpoints, createStylesForBreakpoints, } from '../helpers/breakpoint';
import { styled, css } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
export var InteractionLayerSize;
(function (InteractionLayerSize) {
    InteractionLayerSize["A"] = "A";
    InteractionLayerSize["B"] = "B";
    InteractionLayerSize["C"] = "C";
    InteractionLayerSize["D"] = "D";
    InteractionLayerSize["E"] = "E";
    InteractionLayerSize["F"] = "F";
})(InteractionLayerSize || (InteractionLayerSize = {}));
const animationClassName = 'animation';
const StyledLayerScrollContainer = styled.div.attrs(({ animationDirection, containerHeight }) => ({
    style: { height: animationDirection === 'vertical' ? 'auto' : containerHeight },
})).withConfig({ displayName: "StyledLayerScrollContainer", componentId: "sc-1d9lvrq" }) `overflow-y:auto;-webkit-overflow-scrolling:touch;`;
const widthsMap = {
    A: {
        [Breakpoints.default]: getGridColumn(23),
        [Breakpoints.b560]: getGridColumn(18),
        [Breakpoints.b960]: getGridColumn(12),
        [Breakpoints.b1280]: getGridColumn(8),
        [Breakpoints.b1600]: getGridColumn(6),
        [Breakpoints.b2560]: getColumnsWidthForBreakpoint(6, Breakpoints.b2560),
    },
    B: {
        [Breakpoints.default]: getGridColumn(23),
        [Breakpoints.b560]: getGridColumn(22),
        [Breakpoints.b960]: getGridColumn(20),
        [Breakpoints.b1600]: getGridColumn(19),
        [Breakpoints.b1920]: getGridColumn(18),
        [Breakpoints.b2560]: getColumnsWidthForBreakpoint(18, Breakpoints.b2560),
    },
    C: {
        [Breakpoints.default]: getGridColumn(22),
        [Breakpoints.b560]: getGridColumn(9),
        [Breakpoints.b2560]: getColumnsWidthForBreakpoint(9, Breakpoints.b2560),
    },
    D: {
        [Breakpoints.default]: getGridColumn(24),
        [Breakpoints.b960]: getColumnsWidthForBreakpoint(24, Breakpoints.b960),
    },
    E: {
        [Breakpoints.default]: getGridColumn(23),
        [Breakpoints.b560]: getGridColumn(18),
        [Breakpoints.b960]: getGridColumn(12),
        [Breakpoints.b1600]: getGridColumn(9),
        [Breakpoints.b2560]: getColumnsWidthForBreakpoint(9, Breakpoints.b2560),
    },
    F: {
        [Breakpoints.default]: getGridColumn(24),
        [Breakpoints.b960]: getColumnsWidthForBreakpoint(12, Breakpoints.b960),
    },
};
const heightsMap = {
    A: 'var(--interaction-layer-height)',
    B: 'var(--interaction-layer-height)',
    C: 'var(--interaction-layer-height)',
    D: 'var(--interaction-layer-height)',
    E: 'var(--interaction-layer-height)',
    F: {
        [Breakpoints.default]: '50vh',
        [Breakpoints.b960]: 'var(--interaction-layer-height)',
    },
};
const directionMap = {
    A: 'horizontal',
    B: 'horizontal',
    C: 'horizontal',
    D: 'horizontal',
    E: 'horizontal',
    F: {
        [Breakpoints.default]: 'vertical',
        [Breakpoints.b960]: 'horizontal',
    },
};
function createWidthForBreakpoints(props) {
    const widths = widthsMap[props.size];
    if (!widths) {
        return '';
    }
    return createStylesForBreakpoints(widths, (width) => `width: ${width};`);
}
function createHeightForBreakpoints(props) {
    const heights = heightsMap[props.size];
    if (!heights) {
        return '';
    }
    return createStylesForBreakpoints(heights, (height) => `height: ${height};`);
}
function createDirectionForBreakpoint(size) {
    const direction = directionMap[size];
    const width = typeof window !== 'undefined' ? window.innerWidth || 0 : 0;
    return getValueByScreenSize(width, direction);
}
function createPositionAndTransforms(props) {
    const { animationDirection, direction } = props;
    const horizontalAnimationValue = direction === 'rtl' ? '-100%' : '100%';
    const animationTransform = animationDirection === 'horizontal'
        ? `translate(${horizontalAnimationValue}, 0)`
        : `translate(0, 100%)`;
    return css `bottom:0;left:${direction === 'rtl' ? 0 : 'auto'};right:${direction === 'ltr' ? 0 : 'auto'};&.${animationClassName}{&-enter-active,&-enter-active,&-exit-active,&-exit-active{transition:${(props) => {
        const duration = props.theme.animation.duration.layer;
        const timingFunction = props.theme.animation.timingFunction.in;
        return `transform ${duration} ${timingFunction}`;
    }};}&-enter{transform:${animationTransform};}&-enter-active,&-exit{transform:translate(0);}&-exit-active{transform:${animationTransform};}}`;
}
const StyledLayer = styled.div.withConfig({ displayName: "StyledLayer", componentId: "sc-1wno2sw" }) `background-color:${({ theme }) => theme.colors.background.primary};position:fixed;box-shadow:${({ theme }) => theme.colors.shadow.variant3};overflow-y:auto;-webkit-overflow-scrolling:touch;--interaction-layer-height:${({ height }) => height};${createWidthForBreakpoints} ${createHeightForBreakpoints} ${createPositionAndTransforms};`;
StyledLayer.displayName = 'StyledLayer';
const UnanimatedInteractionLayer = React.forwardRef((props, ref) => {
    const { active, children, direction, size, scrollElementRef, height, ariaLabel, ariaLabelledBy, ariaDescribedBy, } = props;
    const innerRef = ref || undefined;
    const animationDirection = createDirectionForBreakpoint(size);
    return (React.createElement(StyledLayer, { role: "dialog", "aria-modal": active, size: size, ref: innerRef, direction: direction, animationDirection: animationDirection, height: height, "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy },
        React.createElement(StyledLayerScrollContainer, { ref: scrollElementRef, containerHeight: height, direction: direction, animationDirection: animationDirection }, children)));
});
UnanimatedInteractionLayer.displayName = 'UnanimatedInteractionLayer';
const InteractionLayerInternal = (props) => {
    const [height, setHeight] = useThrottle(typeof window !== 'undefined' ? `${window.innerHeight}px` : '100%');
    const handleResize = React.useCallback(() => setHeight(`${window.innerHeight}px`), [setHeight]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    const { active, ariaLabel, ariaDescribedBy, ariaLabelledBy, children, in: visible = true, key, onExited, scrollElementRef, size, theme, } = props;
    const commonProps = getCommonProps(props);
    const direction = theme.direction || 'ltr';
    const timeout = parseInt(theme.animation.duration.layer || '0', 10);
    return (React.createElement(CSSTransition, { classNames: animationClassName, onExited: onExited, in: Boolean(visible), timeout: timeout },
        React.createElement(UnanimatedInteractionLayer, Object.assign({ active: active, size: size, scrollElementRef: scrollElementRef, direction: direction, key: key, height: height, ariaDescribedBy: ariaDescribedBy, ariaLabel: ariaLabel, ariaLabelledBy: ariaLabelledBy }, commonProps), children)));
};
export const InteractionLayer = withTheme(InteractionLayerInternal);
